/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    Divider,
    Field,
    InfoLabel,
    Input,
    InputOnChangeData,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuProps,
    MenuTrigger,
    mergeClasses,
} from '@fluentui/react-components';
import { AddCircle16Regular } from '@fluentui/react-icons';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import React, { MouseEvent, useState } from 'react';

export enum EngagementDocDefaultFields {
    objectives = 'Objectives',
    clientResponsibilities = 'Client Responsibilities',
    servicesProvided = 'Services Provided',
    deliverables = 'Case Deliverables',
    milestones = 'Milestones',
    assumptions = 'Assumptions',
    exclusions = 'Exclusions',
    dependencies = 'Dependencies',
    acceptanceCriteria = 'Acceptance Criteria',
    billingTerms = 'Billing Terms',
    paymentTerms = 'Payment Terms',
}

const useStyles = makeStyles({
    btn: {
        paddingInline: '0px',
    },
    field: {
        width: '100%',
    },
});

//add props with fields in it, and addField, and removeField
export type AddEngagementFieldDropdownProps = {
    fieldNames?: string[];
    addField: (fieldName?: string) => void;
    addFieldDisabled: boolean;
    info?: string;
};
export const AddEngagementFieldDropdown = (props: AddEngagementFieldDropdownProps) => {
    const { addField, fieldNames, addFieldDisabled, info } = props;

    //State
    const [customFieldName, setCustomFieldName] = useState<string>();
    const [isFieldMenuOpen, setIsFieldMenuOpen] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const menuBtnClasses = mergeClasses(commonStyles.button.root, styles.btn);
    const fieldClasses = mergeClasses(styles.field, commonStyles.text.field);

    //Logic
    const onChangeCustomFieldName = (_ev: any, data: InputOnChangeData) => {
        setCustomFieldName(data.value);
    };
    const getDefaultFieldTestId = (fieldName: string) => {
        const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return `add-engagement-field-dropdown-item-${normalizedFieldName}`;
    };

    //UI Events
    const onCustomFieldKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key == 'Enter') {
            e.stopPropagation();

            !!addField && addField(customFieldName);
            setCustomFieldName(undefined);
            setIsFieldMenuOpen(false);
        }
    };
    const onFieldMenuOpenChange: MenuProps['onOpenChange'] = (ev, data) => {
        ev.stopPropagation();

        setIsFieldMenuOpen(data.open);
    };
    const createDefaultFieldClickHandler = (fieldName: string) => (ev: MouseEvent<HTMLDivElement>) => {
        ev.stopPropagation();

        addField(fieldName);
    };

    return (
        <>
            <Menu
                open={isFieldMenuOpen}
                onOpenChange={onFieldMenuOpenChange}
                data-testid='add-engagement-field-dropdown-menu'
            >
                <MenuTrigger disableButtonEnhancement>
                    <InfoLabel info={info}>
                        <MenuButton
                            className={menuBtnClasses}
                            icon={<AddCircle16Regular />}
                            appearance='transparent'
                            data-testid='add-engagement-field-dropdown-trigger'
                            disabled={addFieldDisabled}
                        >
                            Add Field
                        </MenuButton>
                    </InfoLabel>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <Menu key='custom' persistOnItemClick>
                            <MenuTrigger disableButtonEnhancement>
                                <MenuItem data-testid='add-engagement-field-dropdown-custom-item'>Custom Field</MenuItem>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <Field label='Field Name' className={fieldClasses} required>
                                        <Input
                                            value={customFieldName}
                                            onChange={onChangeCustomFieldName}
                                            onKeyUp={onCustomFieldKeyUp}
                                            data-testid='add-engagement-field-dropdown-custom-input'
                                        />
                                    </Field>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                        <Divider key='divider' />
                        {Object.values(EngagementDocDefaultFields).map((val: string) => (
                            <React.Fragment key={val}>
                                {!fieldNames?.includes(val) && (
                                    <MenuItem
                                        onClick={createDefaultFieldClickHandler(val)}
                                        data-testid={getDefaultFieldTestId(val)}
                                    >
                                        {val}
                                    </MenuItem>
                                )}
                            </React.Fragment>
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        </>
    );
};
